import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Award,
  Calendar,
  FileText,
  Edit,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Certification {
  id: number;
  certification_name: string;
  issuing_body: string;
  issue_date: string;
  expiry_date?: string;
  certification_number: string;
  status: 'active' | 'expired' | 'pending';
}

const ProviderCertificationsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCert, setEditingCert] = useState<Certification | null>(null);
  const [formData, setFormData] = useState({
    certification_name: '',
    issuing_body: '',
    issue_date: '',
    expiry_date: '',
    certification_number: '',
  });
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchCertifications();
    }
  }, [user?.id, authLoading]);

  const fetchCertifications = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/certifications`);
      if (response.success && response.data?.certifications) {
        setCertifications(response.data.certifications);
      }
    } catch (error) {
      console.error('Error fetching certifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load certifications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCertification = async () => {
    if (!formData.certification_name || !formData.issuing_body || !formData.issue_date) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      // apiService auto-prefixes /api, so the endpoint here must be
      // /provider/certifications (not /api/provider/certifications). The
      // double-prefix would otherwise trigger the [API GUARD] auto-strip
      // and noisy console.error in config.js.
      const response = await apiService.post(
        `/provider/certifications`,
        editingCert ? { ...formData, id: editingCert.id } : formData
      );
      if (response.success) {
        toast({
          title: 'Success',
          description: editingCert
            ? 'Certification updated successfully'
            : 'Certification added successfully',
        });
        setShowAddModal(false);
        setEditingCert(null);
        setFormData({
          certification_name: '',
          issuing_body: '',
          issue_date: '',
          expiry_date: '',
          certification_number: '',
        });
        fetchCertifications();
      }
    } catch (error) {
      console.error('Error adding certification:', error);
      toast({
        title: 'Error',
        description: 'Failed to save certification',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCertification = async (certId: number) => {
    try {
      const response = await apiService.delete(`/provider/certifications/${certId}`);
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Certification deleted successfully',
        });
        fetchCertifications();
      }
    } catch (error) {
      console.error('Error deleting certification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete certification',
        variant: 'destructive',
      });
    }
  };

  const handleEditCertification = (cert: Certification) => {
    setEditingCert(cert);
    setFormData({
      certification_name: cert.certification_name,
      issuing_body: cert.issuing_body,
      issue_date: cert.issue_date,
      expiry_date: cert.expiry_date || '',
      certification_number: cert.certification_number,
    });
    setShowAddModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const activeCertifications = certifications.filter(c => c.status === 'active').length;
  const expiredCertifications = certifications.filter(c => c.status === 'expired').length;

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Certifications - VireWire Provider</title>
        <meta name="description" content="Manage your professional certifications" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Certifications</h1>
            <p className="text-gray-600">Manage your professional qualifications and certifications.</p>
          </div>
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingCert(null);
                  setFormData({
                    certification_name: '',
                    issuing_body: '',
                    issue_date: '',
                    expiry_date: '',
                    certification_number: '',
                  });
                }}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Certification
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingCert ? 'Edit Certification' : 'Add New Certification'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Certification Name *</Label>
                  <Input
                    id="name"
                    value={formData.certification_name}
                    onChange={(e) =>
                      setFormData({ ...formData, certification_name: e.target.value })
                    }
                    placeholder="e.g., Gas Safe Register"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body">Issuing Body *</Label>
                  <Input
                    id="body"
                    value={formData.issuing_body}
                    onChange={(e) =>
                      setFormData({ ...formData, issuing_body: e.target.value })
                    }
                    placeholder="e.g., Gas Safe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="number">Certification Number</Label>
                  <Input
                    id="number"
                    value={formData.certification_number}
                    onChange={(e) =>
                      setFormData({ ...formData, certification_number: e.target.value })
                    }
                    placeholder="e.g., ABC123456"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="issue">Issue Date *</Label>
                    <Input
                      id="issue"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) =>
                        setFormData({ ...formData, issue_date: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expiry">Expiry Date</Label>
                    <Input
                      id="expiry"
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) =>
                        setFormData({ ...formData, expiry_date: e.target.value })
                      }
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddCertification}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingCert ? 'Update' : 'Add'} Certification
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Total Certifications</p>
              <p className="text-3xl font-bold text-gray-900">{certifications.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Active</p>
              <p className="text-3xl font-bold text-green-600">{activeCertifications}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Expired</p>
              <p className="text-3xl font-bold text-red-600">{expiredCertifications}</p>
            </CardContent>
          </Card>
        </div>

        {/* Certifications List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : certifications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-semibold">No certifications yet</p>
              <p className="text-sm text-gray-500 mt-2">Add your professional certifications to build trust.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {certifications.map((cert) => (
              <Card key={cert.id} className="border border-gray-200 hover:shadow-sm transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <Award className="w-6 h-6 text-blue-600 mt-1" />
                      <div className="flex-1 space-y-2">
                        <p className="font-semibold text-gray-900">{cert.certification_name}</p>
                        <p className="text-sm text-gray-600">{cert.issuing_body}</p>
                        {cert.certification_number && (
                          <p className="text-xs text-gray-500 font-mono">
                            Cert #: {cert.certification_number}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Issued: {new Date(cert.issue_date).toLocaleDateString('en-GB')}
                          </span>
                          {cert.expiry_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Expires: {new Date(cert.expiry_date).toLocaleDateString('en-GB')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(cert.status)}>
                        {cert.status.charAt(0).toUpperCase() + cert.status.slice(1)}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditCertification(cert)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCertification(cert.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderCertificationsPage;
